import React, { useEffect, useState } from 'react'
import { Alert, Button, Checkbox, Flex, Input, List, Modal, Spin, Tag, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { STORE_APP_CONNECTION } from '../../user/permission'
import { useAppCatalog, useAppConnections, useOpenConnection } from '../hooks'
import { AppToolkit } from '../types'
import { useDebouncedSearch } from './app-connections'
import { AppIcon } from './app-icon'
import { LoadMoreOnScroll } from './load-more-on-scroll'

type AppPickerProps = {
    // Apps that already have actions in this campaign.
    usedConnectionIds: string[]
    onCancel: () => void
    onConfirm: (connectionIds: string[]) => void
}

export function AppPicker({ usedConnectionIds, onCancel, onConfirm }: AppPickerProps) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const [selected, setSelected] = useState<string[]>([])
    // Connections that wait for the sign-in in another tab.
    const [awaiting, setAwaiting] = useState<string[]>([])
    const [searchText, setSearchText] = useState('')
    const search = useDebouncedSearch(searchText)
    const connections = useAppConnections({ poll: awaiting.length > 0 })
    const catalog = useAppCatalog(search)
    const connection = useOpenConnection()

    // Select an app as soon as its sign-in in the other tab finishes.
    useEffect(() => {
        const statusOf = (id: string) => connections.data?.find((app) => app.id === id)?.status
        const finished = awaiting.filter((id) => statusOf(id) !== 'pending')

        if (!finished.length) {
            return
        }

        const connected = finished.filter((id) => statusOf(id) === 'connected')

        setAwaiting((current) => current.filter((id) => !finished.includes(id)))
        setSelected((current) => [...new Set([...current, ...connected])])
    }, [awaiting, connections.data])

    const connected = (connections.data ?? []).filter(
        (app) => app.status === 'connected' && app.name.toLowerCase().includes(search.toLowerCase())
    )
    const connectedToolkits = new Set(
        connections.data?.filter((app) => app.status === 'connected').map((app) => app.toolkit)
    )
    const waitingToolkits = new Set(
        connections.data?.filter((app) => awaiting.includes(app.id)).map((app) => app.toolkit)
    )
    const available = (catalog.data?.pages.flatMap((page) => page.items) ?? []).filter(
        (app) => !connectedToolkits.has(app.slug)
    )

    const toggle = (id: string, checked: boolean) =>
        setSelected((current) => (checked ? [...current, id] : current.filter((item) => item !== id)))

    const connect = (toolkit: string) =>
        void connection
            .open(toolkit)
            .then((result) => setAwaiting((current) => [...current, result.id]))
            .catch(() => undefined)

    const confirmLabel =
        selected.length > 1 ? t('Set up {{number}} apps with Evaa', { number: selected.length }) : t('Set up with Evaa')

    return (
        <Modal
            open
            title={t('Add apps')}
            onCancel={onCancel}
            footer={
                <Flex
                    justify="end"
                    gap="small"
                >
                    <Button onClick={onCancel}>{t('Cancel')}</Button>
                    <Button
                        type="primary"
                        disabled={!selected.length}
                        onClick={() => onConfirm(selected)}
                    >
                        {confirmLabel}
                    </Button>
                </Flex>
            }
        >
            <Flex
                vertical
                gap="middle"
            >
                <Typography.Text type="secondary">
                    {t('Choose the apps Evaa may use. Next, Evaa proposes what she can do with them.')}
                </Typography.Text>
                <Input.Search
                    placeholder={t('Search apps')}
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    allowClear
                />
                <Flex
                    vertical
                    className="max-h-[55vh] overflow-y-auto"
                    gap="middle"
                >
                    {connections.isError && (
                        <Alert
                            type="error"
                            message={t('Could not load connected apps')}
                        />
                    )}
                    {connected.length > 0 && (
                        <section aria-label={t('Your apps')}>
                            <Typography.Text strong>{t('Your apps')}</Typography.Text>
                            <List
                                size="small"
                                dataSource={connected}
                                renderItem={(app) => {
                                    const used = usedConnectionIds.includes(app.id)
                                    return (
                                        <List.Item extra={used && <Tag className="!m-0">{t('Already added')}</Tag>}>
                                            <Checkbox
                                                checked={used || selected.includes(app.id)}
                                                disabled={used}
                                                onChange={(event) => toggle(app.id, event.target.checked)}
                                            >
                                                <Flex
                                                    align="center"
                                                    gap="small"
                                                >
                                                    <AppIcon
                                                        name={app.name}
                                                        toolkit={app.toolkit}
                                                        size={24}
                                                    />
                                                    {app.name}
                                                </Flex>
                                            </Checkbox>
                                        </List.Item>
                                    )
                                }}
                            />
                        </section>
                    )}
                    {can(STORE_APP_CONNECTION) && (
                        <section aria-label={t('Connect another app')}>
                            <Flex
                                vertical
                                gap={4}
                            >
                                <Typography.Text strong>{t('Connect another app')}</Typography.Text>
                                <Typography.Text type="secondary">
                                    {t('Sign-in opens in a new tab. When you finish, the app is selected here.')}
                                </Typography.Text>
                                {connection.isError && (
                                    <Alert
                                        type="error"
                                        message={t('Could not connect app. Start a new connection and try again.')}
                                    />
                                )}
                                {connection.redirectUrl && awaiting.length > 0 && (
                                    <Typography.Text type="secondary">
                                        {t('No new tab?')}{' '}
                                        <a
                                            href={connection.redirectUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {t('Open the sign-in page')}
                                        </a>
                                    </Typography.Text>
                                )}
                            </Flex>
                            {catalog.isError && (
                                <Alert
                                    type="error"
                                    message={t('Could not load apps')}
                                />
                            )}
                            <List
                                size="small"
                                loading={catalog.isPending}
                                dataSource={available}
                                renderItem={(app) => (
                                    <ConnectableAppItem
                                        app={app}
                                        waiting={waitingToolkits.has(app.slug)}
                                        connecting={connection.isPending}
                                        onConnect={() => connect(app.slug)}
                                    />
                                )}
                            />
                            <LoadMoreOnScroll
                                hasMore={!!catalog.hasNextPage}
                                loading={catalog.isFetchingNextPage}
                                onLoad={() => void catalog.fetchNextPage()}
                            />
                        </section>
                    )}
                </Flex>
            </Flex>
        </Modal>
    )
}

type ConnectableAppItemProps = {
    app: AppToolkit
    // The sign-in for this app runs in another tab.
    waiting: boolean
    connecting: boolean
    onConnect: () => void
}

function ConnectableAppItem({ app, waiting, connecting, onConnect }: ConnectableAppItemProps) {
    const { t } = useTranslation()

    const action = waiting ? (
        <Flex
            align="center"
            gap="small"
        >
            <Spin size="small" />
            <Typography.Text type="secondary">{t('Waiting for sign-in')}</Typography.Text>
        </Flex>
    ) : (
        <Button
            size="small"
            disabled={connecting}
            onClick={onConnect}
        >
            {t('Connect')}
        </Button>
    )

    return (
        <List.Item extra={action}>
            <List.Item.Meta
                avatar={
                    <AppIcon
                        name={app.name}
                        toolkit={app.slug}
                        logo={app.logo}
                        size={24}
                    />
                }
                title={app.name}
                description={
                    <Typography.Paragraph
                        type="secondary"
                        className="!mb-0"
                        ellipsis={{ rows: 1 }}
                    >
                        {app.description}
                    </Typography.Paragraph>
                }
            />
        </List.Item>
    )
}
